import React from "react";
import { Button, Form } from "react-bootstrap";

// Available on every product, regardless of its brand's specification template.
export default function ProductSizeOptions({ product, setProduct, catalogueDisabled, pricingDisabled }) {
  const options = product.specOptions || [];
  const patch = (index, update) => setProduct((prev) => ({
    ...prev,
    specOptions: (prev.specOptions || []).map((option, i) => i === index ? { ...option, ...update } : option),
  }));
  const patchChoice = (index, choiceIndex, update) => patch(index, {
    choices: options[index].choices.map((choice, i) => i === choiceIndex ? { ...choice, ...update } : choice),
  });
  const addGroup = (label) => setProduct((prev) => {
    const current = prev.specOptions || [];
    let key = label.toLowerCase();
    while (current.some((option) => option.fieldKey === key)) key += "_option";
    return { ...prev, specOptions: [...current, {
      fieldKey: key, label, required: true, choices: [{ value: "", unitPrice: prev.price || "" }],
    }] };
  });
  return (
    <section className="mb-4 p-3 border rounded" aria-label="Sizes and customer choices">
      <h5>Sizes &amp; customer choices</h5>
      <p className="text-muted">Add the sizes this product comes in and the selling price of each size. Customers choose before adding to their cart.</p>
      {options.map((option, index) => (
        <div key={option.fieldKey} className="mb-3 p-3 border rounded">
          <div className="d-flex align-items-center mb-2" style={{ gap: 12 }}>
            <Form.Group className="flex-grow-1">
              <Form.Label>Choice name</Form.Label>
              <Form.Control aria-label={`Choice name ${index + 1}`} value={option.label || option.fieldKey}
                disabled={catalogueDisabled} onChange={(event) => patch(index, { label: event.target.value })} />
            </Form.Group>
            <Button type="button" variant="outline-danger" disabled={catalogueDisabled}
              onClick={() => setProduct((prev) => ({ ...prev, specOptions: prev.specOptions.filter((_, i) => i !== index) }))}>Remove choices</Button>
          </div>
          <Form.Check type="checkbox" id={`product-choice-required-${option.fieldKey}`} label="Required — customer must choose one option" checked={option.required !== false} disabled={catalogueDisabled}
            onChange={(event) => patch(index, { required: event.target.checked })} />
          <Form.Text className="text-muted">{option.required !== false ? "Customers must select one choice before adding this product to their cart." : "Optional: customers can skip this choice."}</Form.Text>
          {(option.choices || []).map((choice, choiceIndex) => (
            <div key={choiceIndex} className="row align-items-end mt-2">
              <Form.Group className="col-md-5">
                <Form.Label>Size or choice</Form.Label>
                <Form.Control required placeholder="e.g. Small (500 g)" aria-label={`${option.label || option.fieldKey} choice ${choiceIndex + 1}`}
                  value={choice.value} disabled={catalogueDisabled}
                  onChange={(event) => patchChoice(index, choiceIndex, { value: event.target.value })} />
              </Form.Group>
              <Form.Group className="col-md-5">
                <Form.Label>Selling price per unit (UGX)</Form.Label>
                <Form.Control type="number" min="1" required={!product.priceOnRequest} disabled={pricingDisabled || product.priceOnRequest}
                  aria-label={`${option.label || option.fieldKey} price ${choiceIndex + 1}`}
                  value={product.priceOnRequest ? "" : choice.unitPrice ?? (Number(product.price || 0) + Number(choice.priceDelta || 0))}
                  onChange={(event) => patchChoice(index, choiceIndex, { unitPrice: event.target.value })} />
              </Form.Group>
              <div className="col-md-2">
                <Button type="button" variant="outline-danger" disabled={catalogueDisabled}
                  aria-label={`Remove ${choice.value || "choice"}`} onClick={() => patch(index, { choices: option.choices.filter((_, i) => i !== choiceIndex) })}>Remove</Button>
              </div>
            </div>
          ))}
          {options.length > 1 && <Form.Text className="text-muted">This price includes the base product price. Adjustments for other selected choices are added to it.</Form.Text>}
          <Button className="mt-2" type="button" variant="outline-primary" disabled={catalogueDisabled}
            onClick={() => patch(index, { choices: [...option.choices, { value: "", unitPrice: product.price || "" }] })}>Add size or choice</Button>
        </div>
      ))}
      {product.priceOnRequest && <p className="text-muted">Prices will be agreed through a quote.</p>}
      <Button type="button" variant="outline-primary" disabled={catalogueDisabled || options.some((o) => o.fieldKey === "size")}
        onClick={() => addGroup("Size")}>Add sizes</Button>{" "}
      <Button type="button" variant="outline-secondary" disabled={catalogueDisabled} onClick={() => addGroup("Colour")}>Add another choice</Button>
    </section>
  );
}
